import { isBrowser } from '@websolutespa/bom-core';
import { LlmApp, LlmCredentials, LlmDecodedThread, LlmHistory, LlmStoredHistory, LlmTest, PostFeedback, PostSummaryOptions } from '../types';
import { MessageDecoder } from './message.service';
import { getMockApp } from './mock';
import { LlmUpload, LlmUploaded, messagesToHistory } from './useLlm';

export type ApiServiceOptions = LlmCredentials & {
  userId?: string;
  threadId?: string;
  endpoint: string | undefined;
  locale: string;
  test?: LlmTest;
  preview?: boolean;
};

export class ApiService {

  options: ApiServiceOptions;

  constructor(options: ApiServiceOptions) {
    this.options = options;
  }

  get apiUrl() {
    const origin = this.options.endpoint || (isBrowser ? window.location.origin : process.env.STORE_URL);
    const apiUrl = `${origin}/api`;
    return apiUrl;
  }

  get baseUrl() {
    const baseUrl = `${this.apiUrl}/llm`;
    return baseUrl;
  }

  async get<T = unknown>(pathname: string) {
    const url = `${this.baseUrl}${pathname}${pathname.indexOf('?') !== -1 ? '&' : '?'}locale=${this.options.locale}`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json() as T;
        return data;
      } else {
        console.error('useLlm.api.get', url, response);
        return undefined;
      }
    } catch (error) {
      console.error('useLlm.api.get', url, error);
      return undefined;
    }
  }

  async post<T = unknown>(pathname: string, payload?: Record<string, unknown>) {
    const options = this.options;
    const url = `${this.baseUrl}${pathname}${pathname.indexOf('?') !== -1 ? '&' : '?'}locale=${options.locale}`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify({
          ...options,
          ...payload,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json() as T;
        return data;
      } else {
        console.error('useLlm.api.post', response, url);
        return undefined;
      }
    } catch (error) {
      console.error('useLlm.api.post', error, url);
      return undefined;
    }
  }

  async fetch<T = unknown>(pathname: string, init: RequestInit) {
    const options = this.options;
    const url = `${this.apiUrl}${pathname}${pathname.indexOf('?') !== -1 ? '&' : '?'}locale=${options.locale}`;
    try {
      const response = await fetch(url, init);
      if (response.ok) {
        const data = await response.json() as T;
        return data;
      } else {
        console.error('useLlm.api.fetch', response, url);
        return undefined;
      }
    } catch (error) {
      console.error('useLlm.api.fetch', error, url);
      return undefined;
    }
  }

  async getInfo(payload?: Partial<ApiServiceOptions>) {
    if (this.options.test) {
      return this.getInfoMock(payload);
    }
    const app = await this.post<LlmApp>('/info', payload);
    return app;
  }

  async upload(upload: LlmUpload): Promise<LlmUploaded | undefined> {
    if (this.options.test) {
      const uploaded: LlmUploaded = {
        id: upload.file.name,
        name: upload.file.name,
        filename: upload.file.name,
        mimeType: upload.file.type,
        filesize: upload.file.size,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        url: upload.file.type.indexOf('image') === 0 ? upload.base64 as string : `//${upload.file.name}`,
        type: upload.file.type.indexOf('image') === 0 ? 'image' : 'file',
      };
      return uploaded;
    }
    const data = new FormData();
    Object.entries(this.options).forEach(entry => {
      data.set(entry[0], String(entry[1]));
    });
    data.set('file', upload.file, upload.file.name);
    const uploaded = await this.fetch<LlmUploaded>('/llmUserUpload/upload', {
      method: 'POST',
      body: data,
    });
    return uploaded && {
      ...uploaded,
      type: uploaded.mimeType.indexOf('image') === 0 ? 'image' : 'file',
    };
  }

  async decorateHistory(storedHistory: LlmStoredHistory[], mode: string = 'default'): Promise<LlmHistory[]> {
    // console.log('ApiService.decoratedHistory.storedHistory', storedHistory);
    if (storedHistory.length > 0) {
      const threadIds = storedHistory.reduce<string[]>((p, c) => {
        if (!p.includes(c.threadId)) {
          p.push(c.threadId);
        }
        return p;
      }, []);
      // console.log('ApiService.decoratedHistory.threadIds', threadIds);
      const decodedThreads: Record<string, LlmDecodedThread> = {};
      for (const threadId of threadIds) {
        const app = await this.getInfo({ threadId });
        // console.log('ApiService.decoratedHistory.thread', app?.thread);
        if (app?.thread) {
          const decodedThread = MessageDecoder.decodeThread(app.thread);
          // console.log('ApiService.decoratedHistory.decodedThread', decodedThread);
          if (decodedThread) {
            decodedThreads[threadId] = decodedThread;
          }
        }
      }
      // console.log('ApiService.decoratedHistory.decodedThreads', decodedThreads);
      const history = storedHistory.map(({ threadId, messageId }, i) => {
        const decodedThread = decodedThreads[threadId];
        if (decodedThread) {
          decodedThread.messages.forEach(x => {
            if (x.role === 'assistant') {
              x.threadId = threadId;
              // this should fix old history missing messageId;
              if (!x.messageId && mode !== 'page') {
                x.messageId = messageId;
              }
            }
          });
          const index = this.options.test ?
            i + 1 :
            decodedThread.messages.findIndex(x => x.messageId === messageId);
          // console.log('ApiService.decoratedHistory.index', index);
          if (index !== -1) {
            const messages = decodedThread.messages.slice(0, index + 1);
            // console.log('ApiService.decoratedHistory.messages', messages);
            if (this.options.test) {
              messages[index].threadId = threadId;
              messages[index].messageId = messageId;
              messages[index].createdAt = new Date().toISOString();
            }
            const histories = messagesToHistory(messages);
            // console.log('ApiService.decoratedHistory.histories', histories);
            return histories[0];
          }
          return undefined;
        }
        return undefined;
      }).filter(x => x !== undefined) as LlmHistory[];
      // console.log('ApiService.decoratedHistory.history', history);
      history.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        }
        return 0;
      });
      if (mode !== 'page') {
        const chatHistory = history.reduce<LlmHistory[]>((p, c) => {
          if (p.findIndex(x => x.threadId === c.threadId) === -1) {
            p.push(c);
          }
          return p;
        }, []);
        // console.log('ApiService.decoratedHistory.chatHistory', chatHistory);
        return chatHistory;
      }
      return history;
    }
    return [];
  }

  async getInfoMock(payload?: Partial<ApiServiceOptions>) {
    const { locale, appKey, apiKey, threadId } = { ...this.options, ...payload };
    if (typeof locale !== 'string') {
      throw { status: 400, message: 'Bad Request: locale is missing' };
    }
    if (!appKey) {
      throw { status: 401, message: 'Unauthorized: appKey is missing' };
    }
    if (!apiKey) {
      throw { status: 401, message: 'Unauthorized: apiKey is missing' };
    }
    const app = getMockApp(locale, threadId, this.options.test);
    return app;
  }

  async postSummary(payload: PostSummaryOptions) {
    if (this.options.test) {
      return this.postSummaryMock(payload);
    }
    const response = await this.post('/summary', payload);
    return response;
  }

  async postSummaryMock(payload: PostSummaryOptions) {
    const { locale, appKey, apiKey, threadId } = this.options;
    if (typeof locale !== 'string') {
      throw { status: 400, message: 'Bad Request: locale is missing' };
    }
    if (!appKey) {
      throw { status: 401, message: 'Unauthorized: appKey is missing' };
    }
    if (!apiKey) {
      throw { status: 401, message: 'Unauthorized: apiKey is missing' };
    }
    if (!threadId) {
      throw { status: 400, message: 'Bad Request: threadId is missing' };
    }
    const { email, html } = payload;
    if (!email) {
      throw { status: 400, message: 'Bad Request: email is missing' };
    }
    if (!html) {
      throw { status: 400, message: 'Bad Request: html is missing' };
    }
    // console.log('/llm/summary', payload);
    return {
      code: 'SENT',
    };
  }

  async postFeedback(feedback: PostFeedback) {
    if (this.options.test) {
      return this.postFeedbackMock(feedback);
    }
    const response = await this.post('/feedback', feedback);
    return response;
  }

  async postFeedbackMock(feedback: PostFeedback) {
    const { locale, appKey, apiKey } = this.options;
    if (typeof locale !== 'string') {
      throw { status: 400, message: 'Bad Request: locale is missing' };
    }
    if (!appKey) {
      throw { status: 401, message: 'Unauthorized: appKey is missing' };
    }
    if (!apiKey) {
      throw { status: 401, message: 'Unauthorized: apiKey is missing' };
    }
    const { threadId, messageId, feedbackRating } = feedback;
    if (!threadId) {
      throw { status: 400, message: 'Bad Request: threadId is missing' };
    }
    if (!messageId) {
      throw { status: 400, message: 'Bad Request: messageId is missing' };
    }
    if (!feedbackRating) {
      throw { status: 400, message: 'Bad Request: feedbackRating is missing' };
    }
    // console.log('/llm/feedback', feedback);
    return {
      code: 'SENT',
    };
  }

  async getHistory(storedHistory: LlmStoredHistory[] = []) {
    if (!this.options.test) {
      try {
        const response = await this.post('/history') as LlmStoredHistory[];
        storedHistory = response;
      } catch (error) {
        console.log('useLlm.getHistory.error', error);
      }
    }
    return storedHistory;
  }

  async addItemToHistory(item: LlmStoredHistory) {
    if (!this.options.test) {
      try {
        await this.post('/historyAdd', item);
      } catch (error) {
        console.log('useLlm.addItemToHistory.error', error);
      }
    }
  }

  async removeItemFromHistory(item: { threadId: string, messageId?: string }) {
    if (!this.options.test) {
      try {
        await this.post('/historyRemove', item);
      } catch (error) {
        console.log('useLlm.removeItemFromHistory.error', error);
      }
    }
  }

}
